// Собственик на имот (само пропърти мениджър): показва текущия и позволява добавяне
// или смяна по телефон. Нерегистриран собственик получава SMS с код за
// вход (важи 24 ч); при смяна старият договор се прекратява.

import React, { useCallback, useState } from 'react';
import { Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { api } from '../../api';
import { showError, showSuccess } from '../../toast';
import { Badge, Button, Card, COLORS, Input, Label, Muted, Row, Screen, Title } from '../../ui';
import { t } from '../../i18n';

export default function AssignOwnerScreen({ route, navigation }: any) {
  const { apartmentId } = route.params;
  const [apartment, setApartment] = useState<any>(null);
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [percent, setPercent] = useState('');
  const [saving, setSaving] = useState(false);

  useFocusEffect(
    useCallback(() => {
      api(`/apartments/${apartmentId}`)
        .then((r) => setApartment(r.apartment))
        .catch((e) => showError(e.message));
    }, [apartmentId])
  );

  const contract = apartment?.managementContracts?.[0];
  const invitation = apartment?.invitations?.[0];
  const hasOwner = !!contract || !!invitation;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api(`/apartments/${apartmentId}/owner`, {
        body: {
          ownerPhone: phone,
          ownerFirstName: firstName || undefined,
          ownerLastName: lastName || undefined,
          ...(percent.trim() ? { managementPercent: Number(percent.replace(',', '.')) } : {}),
        },
      });
      showSuccess(
        r.registered
          ? t('Собственикът е закачен — той е регистриран и получи известие.')
          : r.ownerSmsSent
            ? t('Изпратихме SMS с код за вход (важи 24 часа).')
            : t('Поканата е записана, но SMS-ът не тръгна — проверете номера.')
      );
      navigation.goBack();
    } catch (e: any) {
      showError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!apartment) {
    return (
      <Screen>
        <Title>{t('Собственик')}</Title>
        <Muted>{t('Зареждане…')}</Muted>
      </Screen>
    );
  }

  return (
    <Screen>
      <Title>{t('Собственик')}</Title>
      <Muted>
        {apartment.address}, {apartment.city}
      </Muted>

      {contract && (
        <Card>
          <Label>{t('Текущ собственик')}</Label>
          <Row left={t('Име')} right={`${contract.owner.firstName} ${contract.owner.lastName}`} />
          <Row left={t('Телефон')} right={contract.owner.phone} />
          <Row left={t('Процент')} right={`${Number(contract.managementPercent)}%`} />
        </Card>
      )}
      {!contract && invitation && (
        <Card>
          <Label>{t('Поканен собственик')}</Label>
          <Badge text={t('чака първия си вход')} color={COLORS.warn} />
          <Row left={t('Име')} right={[invitation.firstName, invitation.lastName].filter(Boolean).join(' ') || '—'} />
          <Row left={t('Телефон')} right={invitation.phone} />
        </Card>
      )}

      <Card>
        <Label>{hasOwner ? t('Смяна на собственика') : t('Добавяне на собственик')}</Label>
        {hasOwner && (
          <Muted>{t('Старият договор за управление се прекратява, а старият собственик получава известие.')}</Muted>
        )}
        <Label>{t('Телефон')}</Label>
        <Input value={phone} onChangeText={setPhone} keyboardType="phone-pad" placeholder="+359 88 …" />
        <Label>{t('Име')}</Label>
        <Input value={firstName} onChangeText={setFirstName} placeholder={t('Иван')} />
        <Label>{t('Фамилия')}</Label>
        <Input value={lastName} onChangeText={setLastName} placeholder={t('Иванов')} />
        <Label>{t('Процент за управление (по избор)')}</Label>
        <Input value={percent} onChangeText={setPercent} keyboardType="decimal-pad" placeholder={t('по подразбиране от профила')} />
        <Muted>
          {t('Ако собственикът няма акаунт, получава ЕДИН SMS с код за вход, валиден 24 часа. Регистрираният получава само известие в приложението.')}
        </Muted>
        <Button
          title={hasOwner ? t('Смени собственика') : t('Добави собственик')}
          onPress={save}
          disabled={phone.trim().length < 6}
          loading={saving}
        />
      </Card>
    </Screen>
  );
}
